import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isWeb } from '../utils/platform';
import './GrokAutoPilot.css';

const MAX_TASKS = 50;

const GROK_DEFAULT_MODES = [
  { value: 'tti', label: 'Text to Image' },
  { value: 'iti', label: 'Image to Image' },
  { value: 'ttv', label: 'Text to Video' },
  { value: 'f2v', label: 'Frame to Video' },
  { value: 'ftv', label: 'Ingredients to Video' },
];

const GROK_ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 (Square)' },
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '16:9', label: '16:9 (Landscape)' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
];

const GROK_VIDEO_DURATIONS = [
  { value: '6s', label: '6 seconds' },
  { value: '10s', label: '10 seconds' },
  { value: '6s_concat', label: '6s concat' },
  { value: '10s_concat', label: '10s concat' },
];

const GROK_IMAGE_MODES = [
  { value: 'new_image', label: 'New Image' },
  { value: 'from_last', label: 'From last / extend' },
];

const GROK_VIDEO_RESOLUTIONS = [
  { value: '480p', label: '480p (default)' },
  { value: '720p', label: '720p' },
  { value: '1080p', label: '1080p' },
];

const INGREDIENT_SLOT_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

function newId() {
  return `grok-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function newDraftId() {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Split file / paste into non-empty prompt lines (one prompt per line). */
function linesToPrompts(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !/^[.\-_*=#~\s]+$/.test(line));
}

function joinHintLines(arr) {
  if (!Array.isArray(arr)) return '';
  return arr
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join('\n');
}

/** Electron exposes absolute path on File from the file picker input. */
function filePathFromBrowserFile(file) {
  if (!file) return '';
  const p = file.path;
  return typeof p === 'string' && p.length > 0 ? p : '';
}

function fileUrlForLocalPath(absPath) {
  if (!absPath) return null;
  const normalized = absPath.replace(/\\/g, '/');
  return absPath.startsWith('file://') ? absPath : `file://${normalized}`;
}

const TYPE_LABELS = {
  tti: 'Text → Image',
  iti: 'Image → Image',
  ttv: 'Text → Video',
  f2v: 'Frame → Video',
  ftv: 'Ingredients → Video',
};

function SortableFtvDraftRow({ row, index, slotCount, onPromptChange, onPickSlot, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };
  const paths = Array.isArray(row.imagePaths) ? row.imagePaths : [];

  return (
    <div ref={setNodeRef} style={style} className="grok-ftv-row">
      <span className="grok-ftv-row-index" title="Order in queue (top runs first)">
        {index}
      </span>
      <div className="grok-drag-handle" {...attributes} {...listeners} aria-label="Drag row to reorder jobs">
        ⠿
      </div>
      <div className="grok-ftv-ingredients">
        {Array.from({ length: slotCount }, (_, si) => {
          const p = paths[si] || '';
          const src = p && /\.(png|jpe?g|gif|webp)$/i.test(p) ? fileUrlForLocalPath(p) : null;
          return (
            <div key={si} className="grok-ftv-ingredient-cell">
              {src ? (
                <img src={src} alt="" className="grok-ftv-ingredient-thumb" />
              ) : (
                <span className="grok-ftv-ingredient-placeholder">Img {si + 1}</span>
              )}
              <button
                type="button"
                className="grok-btn grok-btn-secondary grok-ftv-ingredient-pick"
                onClick={() => onPickSlot(row.id, si)}
              >
                Pick…
              </button>
            </div>
          );
        })}
      </div>
      <div className="grok-ftv-row-main">
        <textarea
          value={row.prompt}
          onChange={(e) => onPromptChange(row.id, e.target.value)}
          placeholder={
            slotCount > 1
              ? `One prompt for all ${slotCount} images in this row (Grok ingredients → video)`
              : 'Video / motion prompt for this image'
          }
          aria-label={`Prompt for row ${index}`}
        />
        <div className="grok-ftv-row-actions">
          <button type="button" className="grok-remove" onClick={() => onRemove(row.id)} aria-label="Remove row">
            {'\u00D7'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SortableTask({ task, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  const statusClass =
    task.status === 'completed' ? 'done' : task.status === 'error' ? 'err' : task.status === 'running' ? 'run' : '';

  const thumbSrc =
    task.resultPath && typeof task.resultPath === 'string'
      ? task.resultPath.startsWith('file://')
        ? task.resultPath
        : `file://${task.resultPath.replace(/\\/g, '/')}`
      : null;
  const showThumb = thumbSrc && /\.(png|jpe?g|gif|webp)$/i.test(task.resultPath);
  const refPaths =
    task.type === 'ftv' && Array.isArray(task.referenceImagePaths) && task.referenceImagePaths.length > 0
      ? task.referenceImagePaths
      : task.referenceImagePath
        ? [task.referenceImagePath]
        : [];
  const refPath = refPaths[0];
  const frameSrc = refPath && /\.(png|jpe?g|gif|webp)$/i.test(refPath) ? fileUrlForLocalPath(refPath) : null;
  const showFrameThumb = frameSrc && (task.type === 'ftv' || task.type === 'f2v' || task.type === 'iti');
  const imgMeta =
    task.type === 'ftv' && refPaths.length > 1
      ? ` \u00B7 ${refPaths.length} ingredient images`
      : refPath
        ? ` \u00B7 ref: ${refPath.split(/[/\\]/).pop()}`
        : '';

  return (
    <div ref={setNodeRef} style={style} className="grok-task-item">
      <div className="grok-drag-handle" {...attributes} {...listeners} aria-label="Drag to reorder">
        ⠿
      </div>
      <div className="grok-task-body">
        <div className={`grok-task-status ${statusClass}`}>{(task.status || 'queued').toUpperCase()}</div>
        <div className="grok-task-prompt">{task.prompt}</div>
        <div className="grok-task-meta">
          {TYPE_LABELS[task.type] || task.type}
          {imgMeta}
          {task.error ? ` \u00B7 ${task.error}` : ''}
          {Array.isArray(task.automationLog) && task.automationLog.length > 0
            ? ` \u00B7 automation log: ${task.automationLog.length}`
            : ''}
        </div>
      </div>
      {showFrameThumb ? <img src={frameSrc} alt="" className="grok-frame-thumb" title="First reference" /> : null}
      {showThumb ? <img src={thumbSrc} alt="" className="grok-thumb" /> : null}
      <button type="button" className="grok-remove" onClick={() => onRemove(task.id)} aria-label="Remove task">
        {'\u00D7'}
      </button>
    </div>
  );
}

export default function GrokAutoPilot() {
  const [tasks, setTasks] = useState([]);
  const [delaySeconds, setDelaySeconds] = useState(10);
  const [outputDir, setOutputDir] = useState('');
  const [paused, setPaused] = useState(true);
  const [loading, setLoading] = useState(true);
  const [resumeOffer, setResumeOffer] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [notice, setNotice] = useState('');
  const [queueProgress, setQueueProgress] = useState(null);
  const [delayCountdownSec, setDelayCountdownSec] = useState(null);
  const [newType, setNewType] = useState('f2v');
  const [newRefPath, setNewRefPath] = useState('');
  const [bulkText, setBulkText] = useState('');
  const bulkFileInputRef = useRef(null);
  const f2vMultiInputRef = useRef(null);
  const [ftvDraft, setFtvDraft] = useState([]);
  const [ftvApplyPromptsText, setFtvApplyPromptsText] = useState('');
  const ftvMultiInputRef = useRef(null);
  const [defaultGrokMode, setDefaultGrokMode] = useState('f2v');
  const [defaultAspectRatio, setDefaultAspectRatio] = useState('9:16');
  const [defaultVideoDuration, setDefaultVideoDuration] = useState('6s');
  const [defaultVideoResolution, setDefaultVideoResolution] = useState('480p');
  const [defaultVideoUpscale, setDefaultVideoUpscale] = useState(true);
  const [ingredientsImagesPerPrompt, setIngredientsImagesPerPrompt] = useState(1);
  const [defaultImageInputMode, setDefaultImageInputMode] = useState('new_image');
  const [grokTab, setGrokTab] = useState('inputs');

  const [hintWorkspaceVideo, setHintWorkspaceVideo] = useState('');
  const [hintWorkspaceImage, setHintWorkspaceImage] = useState('');
  const [hintModeTti, setHintModeTti] = useState('');
  const [hintModeIti, setHintModeIti] = useState('');
  const [hintModeTtv, setHintModeTtv] = useState('');
  const [hintModeF2v, setHintModeF2v] = useState('');
  const [hintModeFtv, setHintModeFtv] = useState('');
  const [hintGenAnimate, setHintGenAnimate] = useState('');
  const [hintGenImagine, setHintGenImagine] = useState('');
  const [hintGenFallback, setHintGenFallback] = useState('');
  const [diagLog, setDiagLog] = useState([]);
  const [automationHintsSaved, setAutomationHintsSaved] = useState(false);

  const api = typeof window !== 'undefined' ? window.electronAPI : null;
  const grok = api?.grokAutoPilot;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const applyAutomationOverridesToForm = useCallback((ov) => {
    if (!ov || typeof ov !== 'object') return;
    const mh = ov.modeHints || {};
    setHintWorkspaceVideo(joinHintLines(ov.workspaceVideo));
    setHintWorkspaceImage(joinHintLines(ov.workspaceImage));
    setHintModeTti(joinHintLines(mh.tti));
    setHintModeIti(joinHintLines(mh.iti));
    setHintModeTtv(joinHintLines(mh.ttv));
    setHintModeF2v(joinHintLines(mh.f2v));
    setHintModeFtv(joinHintLines(mh.ftv));
    setHintGenAnimate(joinHintLines(ov.generateAnimate));
    setHintGenImagine(joinHintLines(ov.generateImagine));
    setHintGenFallback(joinHintLines(ov.generateFallback));
  }, []);

  const persistPatch = useCallback(
    async (patch) => {
      if (!grok) return;
      const res = await grok.setState(patch);
      if (res?.success && res.data) {
        const d = res.data;
        if (Array.isArray(d.tasks)) setTasks(d.tasks);
        setPaused(d.paused !== false);
        if (d.outputDir) setOutputDir(d.outputDir);
        if (typeof d.delaySeconds === 'number') setDelaySeconds(d.delaySeconds);
        if (d.defaultGrokMode) {
          setDefaultGrokMode(d.defaultGrokMode);
          setNewType(d.defaultGrokMode);
        }
        if (d.defaultAspectRatio) setDefaultAspectRatio(d.defaultAspectRatio);
        if (d.defaultVideoDuration) setDefaultVideoDuration(d.defaultVideoDuration);
        if (d.defaultVideoResolution) setDefaultVideoResolution(d.defaultVideoResolution);
        if (typeof d.defaultVideoUpscale === 'boolean') setDefaultVideoUpscale(d.defaultVideoUpscale);
        if (typeof d.ingredientsImagesPerPrompt === 'number') {
          setIngredientsImagesPerPrompt(
            Math.min(7, Math.max(1, Math.floor(d.ingredientsImagesPerPrompt)))
          );
        }
        if (d.defaultImageInputMode) setDefaultImageInputMode(d.defaultImageInputMode);
        if (d.automationOverrides) applyAutomationOverridesToForm(d.automationOverrides);
      }
    },
    [grok, applyAutomationOverridesToForm]
  );

  useEffect(() => {
    if (!pendingSession) return;
    if (pendingSession.automationOverrides) {
      applyAutomationOverridesToForm(pendingSession.automationOverrides);
    }
    if (typeof pendingSession.ingredientsImagesPerPrompt === 'number') {
      setIngredientsImagesPerPrompt(
        Math.min(7, Math.max(1, Math.floor(pendingSession.ingredientsImagesPerPrompt)))
      );
    }
    if (pendingSession.defaultVideoResolution) setDefaultVideoResolution(pendingSession.defaultVideoResolution);
    if (typeof pendingSession.defaultVideoUpscale === 'boolean') {
      setDefaultVideoUpscale(pendingSession.defaultVideoUpscale);
    }
  }, [pendingSession, applyAutomationOverridesToForm]);

  const saveAutomationHints = useCallback(async () => {
    if (!grok) return;
    setAutomationHintsSaved(false);
    await persistPatch({
      automationOverrides: {
        workspaceVideo: linesToPrompts(hintWorkspaceVideo),
        workspaceImage: linesToPrompts(hintWorkspaceImage),
        modeHints: {
          tti: linesToPrompts(hintModeTti),
          iti: linesToPrompts(hintModeIti),
          ttv: linesToPrompts(hintModeTtv),
          f2v: linesToPrompts(hintModeF2v),
          ftv: linesToPrompts(hintModeFtv),
        },
        generateAnimate: linesToPrompts(hintGenAnimate),
        generateImagine: linesToPrompts(hintGenImagine),
        generateFallback: linesToPrompts(hintGenFallback),
      },
    });
    setAutomationHintsSaved(true);
    setNotice('Automation hints saved (stored with your Grok queue state).');
  }, [
    grok,
    persistPatch,
    hintWorkspaceVideo,
    hintWorkspaceImage,
    hintModeTti,
    hintModeIti,
    hintModeTtv,
    hintModeF2v,
    hintModeFtv,
    hintGenAnimate,
    hintGenImagine,
    hintGenFallback,
  ]);

  const syncTasks = useCallback(
    async (nextTasks) => {
      setTasks(nextTasks);
      await persistPatch({ tasks: nextTasks });
    },
    [persistPatch]
  );

  useEffect(() => {
    const k = ingredientsImagesPerPrompt;
    setFtvDraft((rows) =>
      rows.map((r) => {
        let paths = Array.isArray(r.imagePaths) ? [...r.imagePaths] : [];
        if (paths.length === 0 && r.framePath) paths = [r.framePath];
        while (paths.length < k) paths.push('');
        return { ...r, imagePaths: paths.slice(0, k) };
      })
    );
  }, [ingredientsImagesPerPrompt]);

  useEffect(() => {
    if (!grok) {
      setLoading(false);
      return;
    }
    let offTask;
    let offPaused;
    let offErr;
    let offDiag;
    let offProgress;
    (async () => {
      const res = await grok.getState();
      setLoading(false);
      if (res?.success && res.data) {
        const d = res.data;
        if (d.tasks?.length > 0) {
          setPendingSession(d);
          setResumeOffer(true);
        } else {
          setTasks(d.tasks || []);
          setDelaySeconds(typeof d.delaySeconds === 'number' ? d.delaySeconds : 10);
          setOutputDir(d.outputDir || '');
          setPaused(d.paused !== false);
          if (d.defaultGrokMode) {
            setDefaultGrokMode(d.defaultGrokMode);
            setNewType(d.defaultGrokMode);
          }
          if (d.defaultAspectRatio) setDefaultAspectRatio(d.defaultAspectRatio);
        if (d.defaultVideoDuration) setDefaultVideoDuration(d.defaultVideoDuration);
        if (d.defaultVideoResolution) setDefaultVideoResolution(d.defaultVideoResolution);
        if (typeof d.defaultVideoUpscale === 'boolean') setDefaultVideoUpscale(d.defaultVideoUpscale);
        if (typeof d.ingredientsImagesPerPrompt === 'number') {
          setIngredientsImagesPerPrompt(
            Math.min(7, Math.max(1, Math.floor(d.ingredientsImagesPerPrompt)))
          );
        }
        if (d.defaultImageInputMode) setDefaultImageInputMode(d.defaultImageInputMode);
        if (d.automationOverrides) applyAutomationOverridesToForm(d.automationOverrides);
        }
      }
    })();

    offTask = grok.onTaskUpdated((data) => {
      if (!data) return;
      setTasks(data.tasks || []);
      setPaused(data.paused !== false);
      if (typeof data.delaySeconds === 'number') setDelaySeconds(data.delaySeconds);
      if (data.outputDir) setOutputDir(data.outputDir);
      if (data.defaultGrokMode) {
        setDefaultGrokMode(data.defaultGrokMode);
        setNewType(data.defaultGrokMode);
      }
      if (data.defaultAspectRatio) setDefaultAspectRatio(data.defaultAspectRatio);
      if (data.defaultVideoDuration) setDefaultVideoDuration(data.defaultVideoDuration);
      if (data.defaultVideoResolution) setDefaultVideoResolution(data.defaultVideoResolution);
      if (typeof data.defaultVideoUpscale === 'boolean') setDefaultVideoUpscale(data.defaultVideoUpscale);
      if (typeof data.ingredientsImagesPerPrompt === 'number') {
        setIngredientsImagesPerPrompt(
          Math.min(7, Math.max(1, Math.floor(data.ingredientsImagesPerPrompt)))
        );
      }
      if (data.defaultImageInputMode) setDefaultImageInputMode(data.defaultImageInputMode);
      if (data.automationOverrides) applyAutomationOverridesToForm(data.automationOverrides);
    });
    offPaused = grok.onQueuePaused((p) => {
      setNotice(p?.message || 'Queue paused (rate limit or manual).');
      setPaused(true);
    });
    offErr = grok.onAutomationError((p) => {
      setNotice(p?.error || 'Automation error.');
    });
    offDiag =
      typeof grok.onAutomationDiagnostics === 'function'
        ? grok.onAutomationDiagnostics((p) => {
            if (!p) return;
            setDiagLog((prev) =>
              [{ ...p, at: new Date().toLocaleTimeString() }, ...prev].slice(0, 14)
            );
          })
        : null;
    offProgress =
      typeof grok.onQueueProgress === 'function'
        ? grok.onQueueProgress((p) => {
            if (!p) return;
            setQueueProgress(p);
          })
        : null;
    return () => {
      if (typeof offTask === 'function') offTask();
      if (typeof offPaused === 'function') offPaused();
      if (typeof offErr === 'function') offErr();
      if (typeof offDiag === 'function') offDiag();
      if (typeof offProgress === 'function') offProgress();
    };
  }, [grok, applyAutomationOverridesToForm]);

  const progressPercent = useMemo(() => {
    const p = queueProgress;
    if (!p || !p.total || p.total < 1) {
      if (p?.phase === 'all_complete') return 100;
      return 0;
    }
    const i = p.index || 1;
    const t = p.total;
    if (p.phase === 'all_complete') return 100;
    if (p.phase === 'task_done' || p.phase === 'task_error') return Math.min(100, Math.round((i / t) * 100));
    if (p.phase === 'running') return Math.min(100, Math.round(((i - 0.5) / t) * 100));
    if (p.phase === 'delay') return Math.min(100, Math.round(((i - 1) / t) * 100 + 30 / t));
    if (p.phase === 'started') return 5;
    return 0;
  }, [queueProgress]);

  useEffect(() => {
    if (!queueProgress || queueProgress.phase !== 'delay' || !queueProgress.delayMs) {
      setDelayCountdownSec(null);
      return;
    }
    const end = Date.now() + queueProgress.delayMs;
    const tick = () => setDelayCountdownSec(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 400);
    return () => clearInterval(id);
  }, [queueProgress]);

  const queueProgressTitle = useMemo(() => {
    const p = queueProgress;
    if (!p) return '';
    if (p.phase === 'delay' && p.total) {
      return `Waiting before prompt ${p.index} of ${p.total}${
        delayCountdownSec != null ? ` (${delayCountdownSec}s)` : ''
      }`;
    }
    if (p.phase === 'running' && p.total) {
      return `Prompt ${p.index} of ${p.total} — processing in Grok…`;
    }
    if (p.phase === 'task_done') return p.message || `Prompt ${p.index} completed.`;
    if (p.phase === 'task_error') return p.message || `Prompt ${p.index} failed.`;
    if (p.phase === 'started') {
      return `Starting queue (${p.queued} job${p.queued === 1 ? '' : 's'})…`;
    }
    if (p.phase === 'all_complete') return p.message || 'All queued jobs finished.';
    if (p.phase === 'paused') return p.message || 'Queue paused.';
    return p.message || '';
  }, [queueProgress, delayCountdownSec]);

  const handleResume = () => {
    if (!pendingSession) return;
    const s = pendingSession;
    setTasks(s.tasks || []);
    setDelaySeconds(typeof s.delaySeconds === 'number' ? s.delaySeconds : 10);
    setOutputDir(s.outputDir || '');
    setPaused(s.paused !== false);
    if (s.defaultGrokMode) {
      setDefaultGrokMode(s.defaultGrokMode);
      setNewType(s.defaultGrokMode);
    }
    if (s.defaultAspectRatio) setDefaultAspectRatio(s.defaultAspectRatio);
    if (s.defaultVideoDuration) setDefaultVideoDuration(s.defaultVideoDuration);
    if (s.defaultVideoResolution) setDefaultVideoResolution(s.defaultVideoResolution);
    if (typeof s.defaultVideoUpscale === 'boolean') setDefaultVideoUpscale(s.defaultVideoUpscale);
    if (typeof s.ingredientsImagesPerPrompt === 'number') {
      setIngredientsImagesPerPrompt(Math.min(7, Math.max(1, Math.floor(s.ingredientsImagesPerPrompt))));
    }
    if (s.defaultImageInputMode) setDefaultImageInputMode(s.defaultImageInputMode);
    if (s.automationOverrides) applyAutomationOverridesToForm(s.automationOverrides);
    setResumeOffer(false);
    setPendingSession(null);
  };

  const handleStartFresh = async () => {
    if (!grok) return;
    const res = await grok.setState({ tasks: [] });
    setTasks([]);
    if (res?.success && res.data) {
      setPaused(res.data.paused !== false);
    }
    setResumeOffer(false);
    setPendingSession(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTasks((items) => {
      const oldIndex = items.findIndex((t) => t.id === active.id);
      const newIndex = items.findIndex((t) => t.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      const next = arrayMove(items, oldIndex, newIndex);
      persistPatch({ tasks: next });
      return next;
    });
  };

  const addBulkTasks = (promptLines) => {
    const lines = Array.isArray(promptLines) ? promptLines : linesToPrompts(promptLines);
    if (lines.length === 0) {
      setNotice('No prompts to add. Use one line per prompt.');
      return;
    }
    if (newType === 'ftv' && ingredientsImagesPerPrompt > 1) {
      setNotice('Bulk lines are not used for multi-image Ingredients — use the batch grid so each row has N images + one prompt.');
      return;
    }
    if ((newType === 'iti' || newType === 'f2v' || (newType === 'ftv' && ingredientsImagesPerPrompt === 1)) && !newRefPath.trim()) {
      setNotice('Pick a shared reference image above first (Image → Image, Frame → Video, or 1-image Ingredients).');
      return;
    }
    const remaining = MAX_TASKS - tasks.length;
    if (remaining <= 0) {
      setNotice(`Queue is full (${MAX_TASKS} tasks). Remove some or clear the queue.`);
      return;
    }
    const slice = lines.slice(0, remaining);
    const skipped = lines.length - slice.length;
    const ref =
      newType === 'iti' || newType === 'f2v' || (newType === 'ftv' && ingredientsImagesPerPrompt === 1)
        ? newRefPath.trim()
        : undefined;
    const added = slice.map((prompt) => ({
      id: newId(),
      type: newType,
      prompt,
      status: 'queued',
      referenceImagePath: ref,
    }));
    const next = [...tasks, ...added];
    syncTasks(next);
    setBulkText('');
    let msg = `Added ${added.length} job${added.length === 1 ? '' : 's'} from prompt lines.`;
    if (skipped > 0) {
      msg += ` ${skipped} line${skipped === 1 ? '' : 's'} skipped (max ${MAX_TASKS} tasks).`;
    }
    setNotice(msg);
  };

  const onBulkFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      setBulkText(normalized);
      addBulkTasks(normalized);
    };
    reader.onerror = () => setNotice('Could not read that file.');
    reader.readAsText(file, 'UTF-8');
  };

  const removeTask = (id) => {
    const next = tasks.filter((t) => t.id !== id);
    syncTasks(next);
  };

  const onDelayBlur = () => {
    const n = parseInt(String(delaySeconds), 10);
    const v = Number.isFinite(n) && n >= 0 ? n : 10;
    setDelaySeconds(v);
    persistPatch({ delaySeconds: v });
  };

  const resumePending = resumeOffer && pendingSession;

  const retryByStatus = useCallback(
    async (statuses, label) => {
      const wanted = new Set(Array.isArray(statuses) ? statuses : []);
      let changed = 0;
      const next = tasks.map((t) => {
        if (!t || !wanted.has(t.status)) return t;
        changed += 1;
        return {
          ...t,
          status: 'queued',
          error: null,
          resultPath: null,
        };
      });
      if (changed === 0) {
        setNotice(`No ${label} tasks to retry.`);
        return;
      }
      await syncTasks(next);
      setNotice(`Re-queued ${changed} ${label} task${changed === 1 ? '' : 's'}. Click Start automation to run them.`);
    },
    [tasks, syncTasks]
  );

  const toggleRun = async () => {
    if (!grok || resumePending) return;
    setNotice('');
    if (paused) {
      const res = await grok.start();
      if (res?.success) {
        setPaused(false);
        if (res?.recoveredRunning > 0) {
          setNotice(
            `Recovered and re-queued ${res.recoveredRunning} interrupted running task${
              res.recoveredRunning === 1 ? '' : 's'
            }.`
          );
        }
      } else {
        setNotice(res?.message || 'Could not start the queue.');
        setQueueProgress(null);
      }
    } else {
      await grok.pause();
      setPaused(true);
    }
  };

  const chooseFolder = async () => {
    if (!grok) return;
    const res = await grok.chooseOutputDir();
    if (res?.success && res.data?.outputDir) {
      setOutputDir(res.data.outputDir);
    }
  };

  const pickRef = async () => {
    if (!grok?.pickReferenceFile) return;
    const res = await grok.pickReferenceFile();
    if (res?.success && res.filePath) {
      setNewRefPath(res.filePath);
    }
  };

  const onBulkBlur = () => {
    const trimmed = String(bulkText || '').trim();
    if (!trimmed) return;
    addBulkTasks(bulkText);
  };

  const onF2vMultiSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const paths = [];
    let missingPath = 0;
    files.forEach((f) => {
      const p = filePathFromBrowserFile(f);
      if (p) paths.push(p);
      else missingPath++;
    });
    if (missingPath > 0) {
      setNotice(`${missingPath} file(s) had no local path. Use the desktop app file picker.`);
    }
    if (paths.length === 0) return;
    const remaining = MAX_TASKS - tasks.length;
    if (remaining <= 0) {
      setNotice(`Queue is full (${MAX_TASKS} tasks). Remove some tasks first.`);
      return;
    }
    const promptLines = linesToPrompts(bulkText);
    const slice = paths.slice(0, remaining);
    const added = slice.map((imgPath, i) => ({
      id: newId(),
      type: 'f2v',
      prompt: promptLines[i] || '',
      status: 'queued',
      referenceImagePath: imgPath,
    }));
    syncTasks([...tasks, ...added]);
    const noPrompt = added.filter((t) => !t.prompt).length;
    let msg = `Added ${added.length} Frame → Video job${added.length === 1 ? '' : 's'} (one per image).`;
    if (noPrompt > 0) msg += ` ${noPrompt} job${noPrompt === 1 ? '' : 's'} have no prompt yet — edit them in the queue.`;
    if (paths.length > remaining) msg += ` ${paths.length - remaining} image(s) skipped (queue limit).`;
    setNotice(msg);
  };

  const handleFtvDraftDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFtvDraft((items) => {
      const oldIndex = items.findIndex((r) => r.id === active.id);
      const newIndex = items.findIndex((r) => r.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const addFtvDraftRow = () => {
    const k = ingredientsImagesPerPrompt;
    setFtvDraft((rows) => [...rows, { id: newDraftId(), imagePaths: Array(k).fill(''), prompt: '' }]);
  };

  const removeFtvDraftRow = (rowId) => {
    setFtvDraft((rows) => rows.filter((r) => r.id !== rowId));
  };

  const updateFtvDraftPrompt = (rowId, prompt) => {
    setFtvDraft((rows) => rows.map((r) => (r.id === rowId ? { ...r, prompt } : r)));
  };

  const pickIngredientSlotForDraftRow = async (rowId, slotIndex) => {
    if (!grok?.pickReferenceFile) return;
    const res = await grok.pickReferenceFile();
    if (res?.success && res.filePath) {
      setFtvDraft((rows) =>
        rows.map((r) => {
          if (r.id !== rowId) return r;
          const next = [...(Array.isArray(r.imagePaths) ? r.imagePaths : [])];
          while (next.length < ingredientsImagesPerPrompt) next.push('');
          next[slotIndex] = res.filePath;
          return { ...r, imagePaths: next.slice(0, ingredientsImagesPerPrompt) };
        })
      );
    }
  };

  const onFtvMultiFramesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const K = ingredientsImagesPerPrompt;
    let missingPath = 0;
    const paths = [];
    files.forEach((f) => {
      const p = filePathFromBrowserFile(f);
      if (p) paths.push(p);
      else missingPath += 1;
    });
    if (missingPath > 0) {
      setNotice(
        `${missingPath} file(s) had no local path. Use the desktop file picker (paths are required for automation).`
      );
    }
    if (paths.length === 0) return;
    if (paths.length % K !== 0) {
      setNotice(
        `Pick a multiple of ${K} images (each group of ${K} becomes one video row). You selected ${paths.length}.`
      );
    }
    const newRows = [];
    for (let i = 0; i + K <= paths.length; i += K) {
      newRows.push({
        id: newDraftId(),
        imagePaths: paths.slice(i, i + K),
        prompt: '',
      });
    }
    if (newRows.length === 0) return;
    setFtvDraft((prev) => [...prev, ...newRows]);
    setNotice(`Added ${newRows.length} ingredient row(s) (${K} image(s) each).`);
  };

  const applyPromptLinesToFtvDraft = () => {
    const lines = linesToPrompts(ftvApplyPromptsText);
    if (lines.length === 0) {
      setNotice('Paste prompts: line 1 → row 1, line 2 → row 2, after you order rows as you want.');
      return;
    }
    setFtvDraft((rows) => {
      const applied = Math.min(lines.length, rows.length);
      const next = rows.map((r, i) => (lines[i] !== undefined ? { ...r, prompt: lines[i] } : r));
      queueMicrotask(() =>
        setNotice(
          applied > 0
            ? `Filled prompts on ${applied} row(s) in list order (row 1 = first line).`
            : 'No rows to fill.'
        )
      );
      return next;
    });
  };

  const addFtvDraftToQueue = async () => {
    const k = ingredientsImagesPerPrompt;
    const complete = ftvDraft.filter((r) => {
      const paths = Array.isArray(r.imagePaths) ? r.imagePaths : [];
      const filled = paths.slice(0, k).every((p) => typeof p === 'string' && p.trim());
      return filled && r.prompt.trim();
    });
    const incompleteCount = ftvDraft.length - complete.length;
    if (complete.length === 0) {
      setNotice(
        `Each row needs all ${k} image slot(s) filled and a prompt (one prompt uses all images in that row for Grok).`
      );
      return;
    }
    const remaining = MAX_TASKS - tasks.length;
    if (remaining <= 0) {
      setNotice(`Queue is full (${MAX_TASKS} tasks).`);
      return;
    }
    const slice = complete.slice(0, remaining);
    const sliceIds = new Set(slice.map((r) => r.id));
    const added = slice.map((r) => {
      const paths = (Array.isArray(r.imagePaths) ? r.imagePaths : []).slice(0, k).filter(Boolean);
      return {
        id: newId(),
        type: 'ftv',
        prompt: r.prompt.trim(),
        status: 'queued',
        referenceImagePaths: paths,
        referenceImagePath: paths[0],
      };
    });
    const skippedComplete = complete.length - slice.length;
    await syncTasks([...tasks, ...added]);
    setFtvDraft((rows) => rows.filter((r) => !sliceIds.has(r.id)));
    let msg = `Added ${added.length} Ingredients → Video job(s) to the queue (same order as this list, top first).`;
    if (incompleteCount > 0) msg += ` Skipped ${incompleteCount} incomplete row(s).`;
    if (skippedComplete > 0) msg += ` ${skippedComplete} pair(s) left in draft (queue limit).`;
    setNotice(msg);
  };

  if (isWeb) {
    return (
      <div className="grok-autopilot">
        <h2>Grok Auto-Pilot</h2>
        <p className="grok-sub">This feature is only available in the Fiddyscript desktop app (Electron).</p>
      </div>
    );
  }

  if (!grok) {
    return (
      <div className="grok-autopilot">
        <h2>Grok Auto-Pilot</h2>
        <p className="grok-sub">Electron bridge not available. Launch the desktop app to use this feature.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grok-autopilot">
        <h2>Grok Auto-Pilot</h2>
        <p className="grok-sub">Loading…</p>
      </div>
    );
  }

  return (
    <div className="grok-autopilot">
      <h2>Grok Auto-Pilot</h2>
      <p className="grok-sub">Queue up to {MAX_TASKS} generation jobs on grok.com. Session is saved; use Peek to log in or fix captchas.</p>

      {resumeOffer && pendingSession ? (
        <div className="grok-resume-banner">
          <span>
            Found saved session ({pendingSession.tasks?.length || 0} task
            {(pendingSession.tasks?.length || 0) === 1 ? '' : 's'}).
          </span>
          <button type="button" className="grok-btn grok-btn-primary" onClick={handleResume}>
            Resume last session
          </button>
          <button type="button" className="grok-btn grok-btn-secondary" onClick={handleStartFresh}>
            Start fresh
          </button>
        </div>
      ) : null}

      {notice ? (
        <div className="grok-notice" role="status">
          {notice}
          <button
            type="button"
            className="grok-btn grok-btn-secondary"
            style={{ marginLeft: '0.75rem', padding: '0.2rem 0.5rem' }}
            onClick={() => setNotice('')}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grok-tabs">
        <div className="grok-tablist" role="tablist" aria-label="Grok Auto-Pilot sections">
          <button
            type="button"
            role="tab"
            id="grok-tab-inputs"
            aria-selected={grokTab === 'inputs'}
            aria-controls="grok-panel-inputs"
            tabIndex={grokTab === 'inputs' ? 0 : -1}
            className={`grok-tab ${grokTab === 'inputs' ? 'grok-tab-active' : ''}`}
            onClick={() => setGrokTab('inputs')}
          >
            Inputs
          </button>
          <button
            type="button"
            role="tab"
            id="grok-tab-settings"
            aria-selected={grokTab === 'settings'}
            aria-controls="grok-panel-settings"
            tabIndex={grokTab === 'settings' ? 0 : -1}
            className={`grok-tab ${grokTab === 'settings' ? 'grok-tab-active' : ''}`}
            onClick={() => setGrokTab('settings')}
          >
            Optional settings
          </button>
          <button
            type="button"
            role="tab"
            id="grok-tab-log"
            aria-selected={grokTab === 'log'}
            aria-controls="grok-panel-log"
            tabIndex={grokTab === 'log' ? 0 : -1}
            className={`grok-tab ${grokTab === 'log' ? 'grok-tab-active' : ''}`}
            onClick={() => setGrokTab('log')}
          >
            Log{diagLog.length > 0 ? ` (${diagLog.length})` : ''}
          </button>
        </div>

        {grokTab === 'inputs' ? (
          <div
            id="grok-panel-inputs"
            role="tabpanel"
            aria-labelledby="grok-tab-inputs"
            className="grok-tab-panel"
          >
            <div className="grok-controls" style={{ marginBottom: resumePending ? '0.75rem' : undefined }}>
              <button type="button" className="grok-btn grok-btn-secondary" onClick={() => grok.togglePeek()}>
                Peek / hide Grok window
              </button>
              <button type="button" className="grok-btn grok-btn-secondary" onClick={chooseFolder}>
                Output folder…
              </button>
            </div>

            {!resumePending ? (
        <>
          <div className="grok-controls">
            <label>
              Delay (s):
              <input
                type="number"
                min={0}
                value={delaySeconds}
                onChange={(e) => {
                  const v = e.target.value;
                  setDelaySeconds(v === '' ? '' : parseInt(v, 10));
                }}
                onBlur={onDelayBlur}
              />
            </label>
            <button
              type="button"
              className={`grok-btn ${paused ? 'grok-btn-primary' : 'grok-btn-danger'}`}
              onClick={toggleRun}
            >
              {paused ? 'Start automation' : 'Pause queue'}
            </button>
            <button
              type="button"
              className="grok-btn grok-btn-secondary"
              onClick={() => retryByStatus(['error'], 'failed')}
            >
              Retry failed
            </button>
            <button
              type="button"
              className="grok-btn grok-btn-secondary"
              onClick={() => retryByStatus(['running'], 'running')}
            >
              Retry running
            </button>
            <button
              type="button"
              className="grok-btn grok-btn-danger"
              title="Remove ALL tasks (queued, running, completed, and failed)"
              onClick={() => {
                if (tasks.length === 0) {
                  setNotice('Queue is already empty.');
                  return;
                }
                const ok = typeof window !== 'undefined' && typeof window.confirm === 'function'
                  ? window.confirm(
                      `Clear ALL ${tasks.length} task${tasks.length === 1 ? '' : 's'} from the queue? ` +
                        'This removes queued, running, completed, and failed tasks. Downloaded files are kept on disk.'
                    )
                  : true;
                if (!ok) return;
                const removed = tasks.length;
                syncTasks([]);
                setNotice(`Cleared ${removed} task${removed === 1 ? '' : 's'} from the queue.`);
              }}
            >
              Clear queue
            </button>
          </div>

          {queueProgress && queueProgress.phase !== 'idle' ? (
            <div className="grok-queue-progress" role="status" aria-live="polite">
              <div className="grok-queue-progress-head">
                <span className="grok-queue-progress-title">{queueProgressTitle}</span>
              </div>
              <div className="grok-queue-progress-track">
                <div
                  className={`grok-queue-progress-fill ${queueProgress.phase === 'delay' ? 'grok-queue-progress-fill-pulse' : ''}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              {queueProgress.promptPreview && (queueProgress.phase === 'delay' || queueProgress.phase === 'running') ? (
                <p className="grok-queue-progress-preview" title={queueProgress.promptPreview}>
                  {queueProgress.promptPreview}
                  {queueProgress.promptPreview.length >= 100 ? '…' : ''}
                </p>
              ) : null}
              <p className="grok-queue-progress-hint">
                The Grok window opens while a job runs (you can hide it again with Peek / hide Grok window). If nothing
                happens, check Optional settings → automation hints or open DevTools on the Grok window.
              </p>
            </div>
          ) : null}

          {outputDir ? (
            <div className="grok-out-dir" title={outputDir}>
              Output: {outputDir}
            </div>
          ) : null}

          <div className="grok-add-row">
            <select value={newType} onChange={(e) => setNewType(e.target.value)} aria-label="Task type">
              <option value="tti">Text → Image</option>
              <option value="iti">Image → Image</option>
              <option value="ttv">Text → Video</option>
              <option value="f2v">Frame → Video</option>
              <option value="ftv">Ingredients → Video</option>
            </select>
            {newType === 'ftv' ? (
              <label className="grok-ingredient-count" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <span>Images / prompt</span>
                <select
                  value={String(ingredientsImagesPerPrompt)}
                  onChange={(e) => {
                    const n = Math.min(7, Math.max(1, parseInt(e.target.value, 10) || 1));
                    setIngredientsImagesPerPrompt(n);
                    persistPatch({ ingredientsImagesPerPrompt: n });
                  }}
                  aria-label="Number of ingredient images per video prompt"
                >
                  {INGREDIENT_SLOT_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {newType === 'iti' || newType === 'f2v' || (newType === 'ftv' && ingredientsImagesPerPrompt === 1) ? (
              <button type="button" className="grok-btn grok-btn-secondary" onClick={pickRef}>
                {newType === 'f2v'
                  ? 'Shared start frame…'
                  : newType === 'ftv'
                    ? 'Shared ingredient image…'
                    : 'Shared reference image…'}
              </button>
            ) : null}
            {newType === 'f2v' ? (
              <>
                <button
                  type="button"
                  className="grok-btn grok-btn-secondary"
                  onClick={() => f2vMultiInputRef.current?.click()}
                  title="Pick multiple images — each image becomes one Frame → Video job, paired with a prompt line if available"
                >
                  Pick multiple frames…
                </button>
                <input
                  ref={f2vMultiInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  aria-hidden
                  tabIndex={-1}
                  style={{ display: 'none' }}
                  onChange={onF2vMultiSelected}
                />
              </>
            ) : null}
          </div>

          {newType === 'ftv' && ingredientsImagesPerPrompt > 1 ? (
            <p className="grok-bulk-hint" style={{ marginBottom: '0.85rem' }}>
              With <strong>{ingredientsImagesPerPrompt}</strong> images per video, build jobs in the{' '}
              <strong>Ingredients batch</strong> below only (each row = {ingredientsImagesPerPrompt} images + one prompt). For
              a single shared image and line-based prompts, set Images / prompt to <strong>1</strong> and use{' '}
              <strong>Prompt lines</strong>.
            </p>
          ) : null}

          {newType === 'iti' || newType === 'f2v' || (newType === 'ftv' && ingredientsImagesPerPrompt === 1) ? (
            <div className="grok-controls" style={{ marginBottom: '0.75rem' }}>
              <span className="grok-out-dir" style={{ maxWidth: 320 }} title={newRefPath}>
                {newRefPath ? newRefPath.split(/[/\\]/).pop() : 'No file — required for prompt lines below'}
              </span>
              {newType === 'f2v' ? (
                <span className="grok-bulk-hint" style={{ margin: 0, fontSize: '0.82em' }}>
                  One job per image, paired with prompt lines above (top image = line 1)
                </span>
              ) : null}
            </div>
          ) : null}

          {!(newType === 'ftv' && ingredientsImagesPerPrompt > 1) ? (
            <div className="grok-bulk">
              <h3>Prompt lines</h3>
              <p className="grok-bulk-hint">
                One line = one queue job. Paste a <strong>single line</strong> for one job, or many lines for batch add. Load
                from <code>.txt</code> / <code>.csv</code> if you like. For Image → Image, Frame → Video, and 1-image
                Ingredients, pick the <strong>shared</strong> image first. Text → Image and Text → Video need no file. Limit{' '}
                {MAX_TASKS} tasks total.
              </p>
              <textarea
                placeholder={'One prompt per line. Example:\nA red barn at sunset\nNeon city in the rain\n\nLines are auto-added to the queue when you click away from this box.'}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                onBlur={onBulkBlur}
                aria-label="Prompts one per line"
              />
              <div className="grok-bulk-actions">
                <button type="button" className="grok-btn grok-btn-secondary" onClick={() => bulkFileInputRef.current?.click()}>
                  Load from file…
                </button>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  aria-hidden
                  tabIndex={-1}
                  onChange={onBulkFileSelected}
                />
              </div>
            </div>
          ) : null}

          {newType === 'ftv' ? (
            <>
              <div className="grok-ftv-guide">
                <strong>Ingredients → Video (same Grok flow as frame → video, but N images per prompt)</strong>
                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                  <li>
                    Grok can take <strong>up to 7 images</strong> for one video prompt. Set <strong>Images / prompt</strong>{' '}
                    above; each row below supplies that many files and <strong>one</strong> text prompt for the whole set.
                  </li>
                  <li>
                    <strong>Frame → Video</strong> is the default single-image case (one start frame + motion prompt).
                  </li>
                  <li>
                    <strong>Order:</strong> row 1 runs first. Drag ⠿ to reorder. &quot;Pick multiple&quot; expects a multiple
                    of N images (N = images per prompt).
                  </li>
                  <li>
                    <strong>Prompt lines:</strong> paste one prompt per row; Apply fills rows top to bottom.
                  </li>
                </ul>
              </div>

              <div className="grok-ftv-draft">
                <h3>Ingredients → Video batch</h3>
                <p className="grok-bulk-hint">
                  Build paired rows, reorder with the handle, then add to the main queue (max {MAX_TASKS} total tasks).
                </p>
                <div className="grok-bulk-actions" style={{ marginBottom: '0.75rem' }}>
                  <button type="button" className="grok-btn grok-btn-secondary" onClick={addFtvDraftRow}>
                    Add empty row
                  </button>
                  <button type="button" className="grok-btn grok-btn-secondary" onClick={() => ftvMultiInputRef.current?.click()}>
                    Pick multiple frames…
                  </button>
                  <input
                    ref={ftvMultiInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
                    multiple
                    aria-hidden
                    tabIndex={-1}
                    onChange={onFtvMultiFramesSelected}
                    style={{ display: 'none' }}
                  />
                </div>

                <p className="grok-bulk-hint" style={{ marginBottom: '0.35rem' }}>
                  Paste prompts (one per line) — applied to rows <strong>from top to bottom</strong>:
                </p>
                <textarea
                  className="grok-ftv-apply-textarea"
                  placeholder={'Line 1 → top row\nLine 2 → second row\n…'}
                  value={ftvApplyPromptsText}
                  onChange={(e) => setFtvApplyPromptsText(e.target.value)}
                  aria-label="Prompt lines to map to rows in order"
                />
                <div className="grok-bulk-actions" style={{ marginBottom: '0.85rem' }}>
                  <button type="button" className="grok-btn grok-btn-secondary" onClick={applyPromptLinesToFtvDraft}>
                    Apply prompts to rows
                  </button>
                  <button type="button" className="grok-btn grok-btn-primary" onClick={addFtvDraftToQueue}>
                    Add paired jobs to queue
                  </button>
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFtvDraftDragEnd}>
                  <SortableContext items={ftvDraft.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                    <div className="grok-ftv-draft-list">
                      {ftvDraft.length === 0 ? (
                        <p className="grok-bulk-hint">No rows yet — add a row or pick multiple images (multiple of N).</p>
                      ) : (
                        ftvDraft.map((row, i) => (
                          <SortableFtvDraftRow
                            key={row.id}
                            row={row}
                            index={i + 1}
                            slotCount={ingredientsImagesPerPrompt}
                            onPromptChange={updateFtvDraftPrompt}
                            onPickSlot={pickIngredientSlotForDraftRow}
                            onRemove={removeFtvDraftRow}
                          />
                        ))
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </>
          ) : null}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <div className="grok-task-list">
                {tasks.map((task) => (
                  <SortableTask key={task.id} task={task} onRemove={removeTask} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
            </>
            ) : (
              <p className="grok-sub">Choose &quot;Resume last session&quot; or &quot;Start fresh&quot; above to continue.</p>
            )}
          </div>
        ) : null}

        {grokTab === 'settings' ? (
          <div
            id="grok-panel-settings"
            role="tabpanel"
            aria-labelledby="grok-tab-settings"
            className="grok-tab-panel"
          >
            <section className="grok-settings-stack" aria-label="Grok automation defaults">
              <h3 className="grok-settings-title">Grok automation defaults</h3>
              <p className="grok-settings-lede">
                These match Grok&rsquo;s on-page options. Automation tries to select them before each run; if Grok changes
                its UI, use Peek (Inputs tab) and adjust. Persisted with your queue state.
              </p>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🎚️
                  </span>
                  <span className="grok-setting-label">Default mode</span>
                </div>
                <select
                  className="grok-setting-select"
                  value={defaultGrokMode}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDefaultGrokMode(v);
                    setNewType(v);
                    persistPatch({ defaultGrokMode: v });
                  }}
                  aria-label="Default mode when adding jobs"
                >
                  {GROK_DEFAULT_MODES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="grok-setting-hint">
                  Matches Grok&rsquo;s modes: Text → Image, Image → Image, Text → Video, Frame → Video, Ingredients → Video.
                  Syncs with the type
                  selector on the Inputs tab; automation selects the Video workspace and sub-mode before each run when
                  possible.
                </p>
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🖼️
                  </span>
                  <span className="grok-setting-label">Default aspect ratio</span>
                </div>
                <select
                  className="grok-setting-select"
                  value={defaultAspectRatio}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDefaultAspectRatio(v);
                    persistPatch({ defaultAspectRatio: v });
                  }}
                  aria-label="Default aspect ratio"
                >
                  {GROK_ASPECT_RATIOS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="grok-setting-hint">
                  Video / frame aspect ratio: 1:1, 9:16, 16:9, 2:3, or 3:2. Default 9:16.
                </p>
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🎬
                  </span>
                  <span className="grok-setting-label">Default video option</span>
                </div>
                <select
                  className="grok-setting-select"
                  value={defaultVideoDuration}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDefaultVideoDuration(v);
                    persistPatch({ defaultVideoDuration: v });
                  }}
                  aria-label="Default video duration"
                >
                  {GROK_VIDEO_DURATIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="grok-setting-hint">
                  Default duration: 6s, 10s, 6s concat, or 10s concat. The <strong>last job</strong> in the queue always
                  uses a plain <strong>6s</strong> or <strong>10s</strong> clip (concat is not applied on the final run).
                </p>
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    📐
                  </span>
                  <span className="grok-setting-label">Default video resolution</span>
                </div>
                <select
                  className="grok-setting-select"
                  value={defaultVideoResolution}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDefaultVideoResolution(v);
                    persistPatch({ defaultVideoResolution: v });
                  }}
                  aria-label="Default video resolution"
                >
                  {GROK_VIDEO_RESOLUTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="grok-setting-hint">
                  Default <strong>480p</strong> keeps generations lighter; automation tries to click the matching quality chip
                  on Grok before each video run (best-effort if labels change).
                </p>
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    {'\u2191\uFE0F'}
                  </span>
                  <span className="grok-setting-label">Video upscale before download</span>
                </div>
                <label className="grok-upscale-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={defaultVideoUpscale}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setDefaultVideoUpscale(v);
                      persistPatch({ defaultVideoUpscale: v });
                    }}
                  />
                  <span>After the first video finishes, try to trigger Grok&rsquo;s upscale / enhance control, then wait again before saving</span>
                </label>
                <p className="grok-setting-hint">
                  If no upscale button is found, the run still completes. Use Peek + Automation recovery hints if Grok renames
                  the control.
                </p>
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🌄
                  </span>
                  <span className="grok-setting-label">Default image mode</span>
                </div>
                <select
                  className="grok-setting-select"
                  value={defaultImageInputMode}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDefaultImageInputMode(v);
                    persistPatch({ defaultImageInputMode: v });
                  }}
                  aria-label="Default image input mode"
                >
                  {GROK_IMAGE_MODES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="grok-setting-hint">
                  Default input mode for image-related flows. Last job in the queue still uses a normal <strong>6s</strong>{' '}
                  or <strong>10s</strong> video setting when concat modes are selected above.
                </p>
              </div>

              <h3 className="grok-settings-title grok-settings-title-spaced">Automation recovery</h3>
              <p className="grok-settings-lede">
                When Grok renames tabs or buttons, add hints here (saved in <code>grok_state.json</code>). One match per line:
                substring (case-insensitive) or regex <code>/^Video$/i</code>. Built-in patterns run first; these run after.
              </p>
              <p className="grok-settings-lede" style={{ marginTop: '0.65rem' }}>
                <strong>Strict automation:</strong> <code className="grok-inline-code">GENERATION_MODE_NOT_FOUND</code> means no
                mode chip matched your job type after built-ins and your Mode chips hints. A dump of visible chip-like labels
                is stored on that task&rsquo;s persisted <code className="grok-inline-code">automationLog</code> (Log tab →
                Task automation logs). Add or adjust lines under <strong>Mode chips</strong> for that type.{' '}
                <code className="grok-inline-code">GENERATION_START_NOT_DETECTED</code> means no start signal (busy text,
                progress UI, etc.) within ~20s after Generate — the queue may retry; use Peek to confirm Grok accepted the
                job. For <strong>Frame → Video</strong>, the composer <strong>Video</strong> chip (not <strong>Image</strong>)
                must be active: Image is for static image output; reference frames attach under Video.
              </p>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🧭
                  </span>
                  <span className="grok-setting-label">Workspace tabs</span>
                </div>
                <label className="grok-hint-label">Video workspace (Text / Frame / Ingredients → Video)</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintWorkspaceVideo}
                  onChange={(e) => setHintWorkspaceVideo(e.target.value)}
                  placeholder={'e.g. Grok Video\n/^video$/i'}
                  rows={3}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Image workspace (Text / Image → Image)</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintWorkspaceImage}
                  onChange={(e) => setHintWorkspaceImage(e.target.value)}
                  placeholder={'e.g. Imagine\nImage'}
                  rows={3}
                  spellCheck={false}
                />
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    🎯
                  </span>
                  <span className="grok-setting-label">Mode chips (per task type)</span>
                </div>
                <label className="grok-hint-label">Text → Image</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintModeTti}
                  onChange={(e) => setHintModeTti(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Image → Image</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintModeIti}
                  onChange={(e) => setHintModeIti(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Text → Video</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintModeTtv}
                  onChange={(e) => setHintModeTtv(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Frame → Video</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintModeF2v}
                  onChange={(e) => setHintModeF2v(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Ingredients → Video</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintModeFtv}
                  onChange={(e) => setHintModeFtv(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
              </div>

              <div className="grok-setting-card">
                <div className="grok-setting-card-head">
                  <span className="grok-setting-icon" aria-hidden>
                    {'\u25B6\uFE0F'}
                  </span>
                  <span className="grok-setting-label">Generate buttons</span>
                </div>
                <label className="grok-hint-label">Ingredients / Animate (runs first for Ingredients → Video)</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintGenAnimate}
                  onChange={(e) => setHintGenAnimate(e.target.value)}
                  placeholder={'Animate\n/^make video$/i'}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Imagine / Create (Text → Image, Image → Image, Text → Video)</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintGenImagine}
                  onChange={(e) => setHintGenImagine(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <label className="grok-hint-label">Fallback (any type, last resort)</label>
                <textarea
                  className="grok-hint-textarea"
                  value={hintGenFallback}
                  onChange={(e) => setHintGenFallback(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
                <div className="grok-bulk-actions" style={{ marginTop: '0.75rem' }}>
                  <button type="button" className="grok-btn grok-btn-primary" onClick={saveAutomationHints}>
                    Save automation hints
                  </button>
                  {automationHintsSaved ? (
                    <span className="grok-bulk-hint" style={{ alignSelf: 'center' }}>
                      Saved.
                    </span>
                  ) : null}
                </div>
              </div>

            </section>
          </div>
        ) : null}

        {grokTab === 'log' ? (
          <div
            id="grok-panel-log"
            role="tabpanel"
            aria-labelledby="grok-tab-log"
            className="grok-tab-panel"
          >
            <div className="grok-setting-card" style={{ margin: '1rem 0' }}>
              <div className="grok-setting-card-head">
                <span className="grok-setting-icon" aria-hidden>📋</span>
                <span className="grok-setting-label">Run diagnostics</span>
              </div>
              <p className="grok-setting-hint">
                Each run appends a JSON entry below. The same rows are merged into each task&rsquo;s{' '}
                <code className="grok-inline-code">automationLog</code> in <code className="grok-inline-code">grok_state.json</code>{' '}
                (including <code className="grok-inline-code">chipDump</code> when mode matching fails). Check the{' '}
                <code className="grok-inline-code">diag</code>-shaped fields for <code className="grok-inline-code">step</code>,{' '}
                <code className="grok-inline-code">detail</code>, and clicked <code className="grok-inline-code">label</code> /{' '}
                <code className="grok-inline-code">tag</code>.
              </p>
              {diagLog.length === 0 ? (
                <p className="grok-bulk-hint">No traces yet — run the queue to populate.</p>
              ) : (
                <pre className="grok-diag-log" tabIndex={0}>
                  {diagLog.map((entry, i) => (
                    <React.Fragment key={i}>
                      {JSON.stringify(entry, null, 2)}
                      {'\n\n'}
                    </React.Fragment>
                  ))}
                </pre>
              )}
              <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button type="button" className="grok-btn grok-btn-secondary" onClick={() => setDiagLog([])}>
                  Clear session trace
                </button>
                <button
                  type="button"
                  className="grok-btn grok-btn-secondary"
                  disabled={!grok}
                  onClick={() => persistPatch({ clearTaskAutomationLogs: true })}
                >
                  Clear persisted task logs
                </button>
              </div>
            </div>

            <div className="grok-setting-card" style={{ margin: '1rem 0' }}>
              <div className="grok-setting-card-head">
                <span className="grok-setting-icon" aria-hidden>
                  📋
                </span>
                <span className="grok-setting-label">Task automation logs</span>
              </div>
              <p className="grok-setting-hint">
                Persisted per task when automation runs. Expand a job to inspect rows; mode failures include a{' '}
                <code className="grok-inline-code">chipDump</code> array of visible control labels Grok showed when matching
                failed.
              </p>
              {tasks.filter((t) => Array.isArray(t.automationLog) && t.automationLog.length > 0).length === 0 ? (
                <p className="grok-bulk-hint">No task logs yet — they appear after at least one automation attempt.</p>
              ) : (
                <div className="grok-task-automation-logs">
                  {tasks
                    .filter((t) => Array.isArray(t.automationLog) && t.automationLog.length > 0)
                    .map((t) => {
                      const preview = (t.prompt || '').trim().slice(0, 88);
                      const tail = (t.prompt || '').trim().length > 88 ? '\u2026' : '';
                      return (
                        <details key={t.id} className="grok-task-automation-log-item">
                          <summary className="grok-task-automation-log-summary">
                            <span className="grok-task-automation-log-status">{(t.status || 'queued').toUpperCase()}</span>
                            {preview}
                            {tail}
                            <span className="grok-task-automation-log-count">
                              {' '}
                              ({t.automationLog.length} row{t.automationLog.length === 1 ? '' : 's'})
                            </span>
                          </summary>
                          <pre className="grok-diag-log grok-task-automation-log-pre" tabIndex={0}>
                            {JSON.stringify(t.automationLog, null, 2)}
                          </pre>
                        </details>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
